import { InputAdornment, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import moment from 'moment-timezone';
import React from 'react';
import FormTextField from '../../../components/FormTextField';
import TableSkeleton from '../../../components/TableSkeleton';
import { CoinUnitPriceType } from '../../../services/api/CoinUnitPrice';
import { ICoinUnitPrice } from '../../../services/types';

interface IProps {
  type: CoinUnitPriceType.InProgress | CoinUnitPriceType.ToBeFinalized | CoinUnitPriceType.Finalized;
  coinUnitPrices: ICoinUnitPrice[];
  onRevenueChange?: (price: ICoinUnitPrice) => void;
  isLoading?: boolean;
}

const CoinUnitPriceTable = (props: IProps): JSX.Element => {
  const { type, coinUnitPrices, onRevenueChange = () => {}, isLoading = false } = props;

  const handleRevenueChange = (coinUnitPrice: ICoinUnitPrice, from: 'google' | 'apple' | 'video', value: number) => {
    const cloned: ICoinUnitPrice = Object.assign({}, coinUnitPrice, { [from]: value });
    cloned.unitPrice = 0;
    onRevenueChange(cloned);
  };

  return (
    <Table>
      <TableHead style={{ verticalAlign: 'middle' }}>
        <TableCell align="center">Date</TableCell>

        {type === CoinUnitPriceType.ToBeFinalized && (
          <>
            <TableCell align="right">Google</TableCell>
            <TableCell align="right">Apple</TableCell>
            <TableCell align="right">In-App Ads</TableCell>
          </>
        )}

        {type === CoinUnitPriceType.Finalized && (
          <>
            <TableCell align="right">Coin Unit Price</TableCell>
            <TableCell align="right">Revenue</TableCell>
          </>
        )}
        <TableCell align="right">Radish Plus Pool</TableCell>
        <TableCell align="right">Total Issued Coins</TableCell>
        <TableCell align="right"># of Credited Coins</TableCell>
        <TableCell align="right"># of One-time Coins</TableCell>
        <TableCell align="right"># of Subscription Coins</TableCell>
        <TableCell align="right"># of Rewarded video Coins</TableCell>
        <TableCell align="right"># of Offerwall Coins</TableCell>
        <TableCell align="right"># of Free Coins</TableCell>
        <TableCell align="right"># of Radish Plus Coins</TableCell>
      </TableHead>
      <TableBody>
        {isLoading && <TableSkeleton rows={12} columns={10} />}
        {!isLoading &&
          coinUnitPrices.map((coinUnitPrice: ICoinUnitPrice) => {
            const { id, month, google, apple, video, unitPrice, coins, radishPlusPool } = coinUnitPrice;
            const {
              credited,
              subscription,
              rewarded_video,
              offerwall,
              giveaway,
              welcome,
              earned,
              'one-time': oneTime,
              'radish-plus': radishPlus,
            } = coins;

            const revenue = google + apple + video;
            const freeCoins = giveaway + welcome + earned;
            const totalIssuedCoins = credited + oneTime + subscription + rewarded_video + offerwall + freeCoins + (radishPlus ?? 0);

            return (
              <TableRow key={id} style={{ height: 52, verticalAlign: 'middle' }}>
                <TableCell align="center">{moment(month, 'YYYYMM').format('MMM YYYY')}</TableCell>

                {type === CoinUnitPriceType.ToBeFinalized && (
                  <>
                    <TableCell align="right">
                      <FormTextField
                        variant="outlined"
                        defaultValue={String(google)}
                        onValueSubmit={(value: string) => handleRevenueChange(coinUnitPrice, 'google', Number(value))}
                        size="small"
                        style={{ width: '120px', height: '34px' }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        error={!google}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <FormTextField
                        variant="outlined"
                        defaultValue={String(apple)}
                        onValueSubmit={(value: string) => handleRevenueChange(coinUnitPrice, 'apple', Number(value))}
                        size="small"
                        style={{ width: '120px', height: '34px' }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        error={!apple}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <FormTextField
                        variant="outlined"
                        defaultValue={String(video)}
                        onValueSubmit={(value: string) => handleRevenueChange(coinUnitPrice, 'video', Number(value))}
                        size="small"
                        style={{ width: '120px', height: '34px' }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        error={!video}
                      />
                    </TableCell>
                  </>
                )}

                {type === CoinUnitPriceType.Finalized && (
                  <>
                    <TableCell align="right">
                      {Number(unitPrice).toLocaleString('en-US', {
                        maximumFractionDigits: 5,
                      })}
                    </TableCell>
                    <TableCell align="right">{`$${Number(revenue).toLocaleString()}`}</TableCell>
                  </>
                )}

                <TableCell align="right">{`${radishPlusPool ? Number(radishPlusPool).toLocaleString() : 0}`}</TableCell>
                <TableCell align="right">{Number(totalIssuedCoins).toLocaleString()}</TableCell>
                <TableCell align="right">{Number(credited).toLocaleString()}</TableCell>
                <TableCell align="right">{Number(coins['one-time']).toLocaleString()}</TableCell>
                <TableCell align="right">{Number(subscription).toLocaleString()}</TableCell>
                <TableCell align="right">{Number(rewarded_video).toLocaleString()}</TableCell>
                <TableCell align="right">{Number(offerwall).toLocaleString()}</TableCell>
                <TableCell align="right">{Number(freeCoins).toLocaleString()}</TableCell>
                <TableCell align="right">{`${radishPlus ? Number(radishPlus).toLocaleString() : 0}`}</TableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  );
};

export default CoinUnitPriceTable;
